import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import type { PageInfo } from './useControlAreaNavigation';

/**
 * スクロールハンドラーの戻り値の型
 */
export interface ScrollHandlers {
  handlePrevPage: () => void;
  handleNextPage: () => void;
  handleScrollToTop: () => void;
}

/**
 * スクロールコンテナを取得
 */
function getScrollContainer(): HTMLElement | null {
  return document.querySelector('[data-scroll-container="true"]') as HTMLElement;
}

/**
 * スムーズにスクロールを実行
 */
function smoothScroll(container: HTMLElement | null, top: number): void {
  if (!container) {
    return;
  }

  // 即座にスクロール開始
  container.scrollTo({ top, behavior: 'smooth' });

  // 150ms後に再度スクロール（レンダリング完了を待つ）
  setTimeout(() => {
    container.scrollTo({ top, behavior: 'smooth' });
  }, 150);
}

/**
 * ControlAreaのスクロール制御を提供するカスタムフック
 *
 * @param pageInfo - ページ情報
 * @returns スクロールハンドラー
 */
export function useControlAreaScroll(pageInfo: PageInfo | null): ScrollHandlers {
  const navigate = useNavigate();

  const handlePrevPage = useCallback((): void => {
    if (pageInfo?.prevPath) {
      navigate(pageInfo.prevPath);
      smoothScroll(getScrollContainer(), 0);
    }
  }, [pageInfo?.prevPath, navigate]);

  const handleNextPage = useCallback((): void => {
    if (pageInfo?.nextPath) {
      navigate(pageInfo.nextPath);
      smoothScroll(getScrollContainer(), 0);
    }
  }, [pageInfo?.nextPath, navigate]);

  const handleScrollToTop = useCallback((): void => {
    smoothScroll(getScrollContainer(), 0);
  }, []);

  return {
    handlePrevPage,
    handleNextPage,
    handleScrollToTop,
  };
}
